/**
 * @file 公用组件
 */
import {getSourceArray} from './markUtil';
import {ElMessageBox as MessageBox} from '@/lib/element-polyfill';
import {AccountTypes, UserTypeEnum} from '../enmus/UserType';
import * as RouterUtil from '@/router/routerUtil';
import * as PageNames from '@/enmus/PageNames';
import {LoginTypeCookieKey} from '@/constant';
import {logoutUrl as passportLogoutUrl} from './passportUtil';
import {logoutUrl as uuapLogoutUrl} from './uuapUtil';

/**
 * 自定计算Y轴分割刻度值
 * @param {*} max 数据最大值
 * @param {*} min 数据最小值
 * @param {*} splitNumber 分割个数
 */
export function reactAxisSplit(min, max, splitNumber) {
    if (max - min < splitNumber) {
        return {
            max: max < splitNumber ? splitNumber : max,
            min: max - splitNumber < 0 ? 0 : max - splitNumber,
            split: 1
        };
    }
    const unround = (max - min) / splitNumber;   // 等分刻度
    const x = Math.ceil(Math.log10(unround));
    const pow10x = Math.pow(10, x);
    // const arr = [0.1, 0.2, 0.25, 0.3, 0.4, 0.5, 0.6, 0.7, 0.75, 0.8, 0.9, 1];
    let split;
    let init = false;
    for (let i = 0; i <= 1.01; i = i + 0.01) {
        if (unround / pow10x <= i) {
            const s = Math.round(i * pow10x);
            if (!init) {
                split = s;
                init = true;
            }
            const minV = s * Math.floor(min / s);
            const maxV = s * Math.ceil(max / s);
            const sNumber = parseInt((maxV - minV) / s);
            if (sNumber === splitNumber) {
                return {
                    max: maxV,
                    min: minV,
                    split: s
                };
            }
        }
    }
    return {
        max: split * Math.ceil(max / split),
        min: split * Math.floor(min / split),
        split
    };
}

/**
 * 设置cookie
 *
 * @param {string} key cookie的key
 * @param {string} value cookie的value
 * @param {string} days cookie的时长
 *
 */
export function setCookie(key, value, days) {
    let date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = key + '=' + encodeURIComponent(value) + ';expires=' + date.toUTCString() + ';path=/';
}

/**
 * 获取cookie
 *
 * @param {string} key cookie的key
 *
 * @return {string} value cookie的value
 *
 */
export function getCookie(key) {
    let arr1 = document.cookie.split('; ');
    for (let i = 0, len = arr1.length; i < len; i++) {
        let arr2 = arr1[i].split('=');
        if (arr2[0] === key) {
            return decodeURIComponent(arr2[1]);
        }
    }
}

/**
 * 删除cookie
 *
 * @param {string} key  cookie的key
 *
 */
export function eraseCookie(name) {
    document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;";
}

/**
 * 获取流量来源的数组
 *
 * @return {Array.<string>} res 流量来源
 *
 */
export function getFilters() {
    let res = [];
    const sourceArray = getSourceArray();
    for (let item in sourceArray) {
        if (sourceArray[item] !== 'Amazon' && sourceArray[item] !== '无头浏览器') {
            res.push({
                text: sourceArray[item],
                value: sourceArray[item]
            });
        }
    }
    return res;
}

/**
 * 对比两个array的内容是否相同
 *
 * @param {Array} arr1 数组1
 * @param {Array} arr2 数组2
 *
 * @return {boolean} res 相同为true, 不同为false
 *
 */
export function compareArray(arr1, arr2) {
    if (!arr1 || !arr2) {
        return false;
    }

    // compare lengths - can save a lot of time
    if (arr1.length !== arr2.length) {
        return false;
    }

    for (let i = 0, l = arr1.length; i < l; i++) {
        // Check if we have nested arrays
        if (arr1[i] instanceof Array && arr2[i] instanceof Array) {
            // recurse into the nested arrays
            if (!arr1[i].equals(arr2[i])) {
                return false;
            }
        } else if (arr1[i] !== arr2[i]) {
            // Warning - two different object instances will never be equal: {x:20} != {x:20}
            return false;
        }
    }
    return true;
}

/***
 * 数字字符串格式化，每隔三位小数加逗号。
 * @param {string} numStr 数字的字符串格式。
 * @return {string} 格式化后的数字字符串。
 */
export function addCommaToThousands(numStr) {
    numStr = numStr + '';
    let [interge, decimals] = numStr.split('.');
    let result = '';
    while (interge.length > 3) {
        result = ',' + interge.slice(-3) + result;
        interge = interge.slice(0, -3);
    }
    if (interge.length > 0) {
        result = interge + result;
    }
    if (decimals) {
        return `${result}.${decimals.substr(0, 2)}`;
    }
    return result;
}

/***
 * 获取10px字体，带逗号格式化的数字字符串的长度。
 * @param {string} numStr。
 * @return {int} 长度，单位：px。
 */
export function getNumPixelLength(numStr) {
    numStr = numStr + '';
    let len = 0;
    for (let i = 0; i < numStr.length; i++) {
        if (numStr[i] >= '0' && numStr[i] <= '9') {
            len += 6;
        } else if (numStr[i] === ',') {
            len += 4;
        }
    }
    return len;
}

/**
 * 获得符合条件的字符串，与其实际长度
 * @param {string} str 输入的字符串
 * @param {number} limit 限制的字符串长度，中文算2个字符，英文算1个字符，eg: "啊a".length = 3
 * @return {Object}
 */
export function getLimitedStr(str, limit) {
    let obj = {
        str: str,
        len: 0
    };
    let realLength = 0;
    let len = str.length;
    let charCode = -1;
    for (let i = 0; i < len; i++) {
        let oldLen = realLength;
        charCode = str.charCodeAt(i);
        if (charCode >= 0 && charCode <= 128) {
            realLength += 1;
        } else {
            realLength += 2;
        }
        if (realLength > limit) {

            obj.str = str.substring(0, i);
            obj.len = oldLen;
            return obj;
        }
    }
    obj.len = realLength;
    return obj;
}

/**
 * 根据用户角色返回是否百度用户。
 * @param {string} userType 用户角色。
 * @return {boolean} 是否百度用户。
 */
export function isBaiduerType(userType) {
    return userType === UserTypeEnum.BAIDU_MANAGER || userType === UserTypeEnum.BAIDU_COMMON;
}

/**
 * 根据用户角色返回是否管理员用户。
 * @param {string} userType 用户角色。
 * @return {boolean} 是否管理员用户。
 */
export function isManager(userType) {
    return userType === UserTypeEnum.BAIDU_MANAGER || userType === UserTypeEnum.CUSTOMER_MANAGER;
}

/**
 * 判断是否只包含数字、英文、中文
 * @param {string} str
 * @return {boolean} 判断结果
 */
export function testNumberEnglishChinese(str) {
    return !(/[^a-zA-Z0-9\u4E00-\u9FA5]/.test(str))
}

/***
 * 检查浏览器版本等问题
 * @return {boolean} 如果是chrome或者safari则返回true，否则false。
 */
// export function checkBrowser() {
//     const browserName = browser && browser.name || '';
//     const allowBrowses = ['chrome', 'safari'];
//     return allowBrowses.indexOf(browserName) > -1;
// }

/***
 * 元素相对于body的offsetTop
 * @return {Object}
 */
export function getOffsetByBody(el) {
    let offset = {
        top: 0,
        left: 0
    };
    while (el && el.tagName !== 'BODY') {
        offset.top += el.offsetTop;
        offset.left += el.offsetLeft;
        el = el.offsetParent;
    }
    return offset;
}
/***
 * 判断child与parent的父子关系
 * @return {boolean} true 是，false 否
 */

export function isChildOf(child, parent) {
    let parentNode;
    if (child && parent) {
        parentNode = child.parentNode;
        while (parentNode) {
            if (parent === parentNode) {
                return true;
            }
            parentNode = parentNode.parentNode;
        }
    }
    return false;
}

/***
 * 判断dom是否有类名
 * @param {Element} elements 修改节点
 * @param {string} cName 类名
 * @return {boolean}
 */
export function hasClass(elements, cName) {
    return !!elements.className.split(' ').find(className => {
        return className && className === cName;
    });
}

/***
 * 添加class类名
 * @param {Element} elements 修改节点
 * @param {string} cName 类名
 */
export function addClass(elements, cName) {
    if (!hasClass(elements, cName)) {
        elements.className += ' ' + cName;
    }
}

/***
 * 删除class类名
 * @param {Element} elements 修改节点
 * @param {string} cName 类名
 */
export function removeClass(elements, cName) {
    if (hasClass(elements, cName)) {
        elements.className = elements.className.replace(new RegExp('(\\s|^)' + cName + '(\\s|$)'), ' ');
    }
}

/**
 * 十六进制颜色转rbga
 * @param {string} str 十六进制颜色字符串
 * @param {number} opacity 透明度
 */
// export function color2Rgba(str, opacity){
//     //十六进制颜色值的正则表达式
//     const reg = /^#([0-9a-fA-f]{3}|[0-9a-fA-f]{6})$/;
//     let sColor = str.toLowerCase();
//     if (sColor && reg.test(sColor)) {
//         if(sColor.length === 4){
//             let sColorNew = "#";
//             for(let i = 1; i < 4; i += 1){
//                 sColorNew += sColor.slice(i, i + 1).concat(sColor.slice(i, i + 1));
//             }
//             sColor = sColorNew;
//         }
//         //处理六位的颜色值
//         const sColorChange = [];
//         for(let i = 1; i < 7; i += 2){
//             sColorChange.push(parseInt("0x" + sColor.slice(i, i + 2)));
//         }
//         return `rgba(${sColorChange.join(",")}, ${opacity})`;
//     } else {
//         return sColor;
//     }
// }

/**
 * 清除登录的cookie
 */
export function clearLoginCookie() {
    eraseCookie('selectedSubId');
    eraseCookie('selectedSupplyId');
    eraseCookie(LoginTypeCookieKey);
}
/**
 * 弹出消息并跳转登录页
 * @param {string} message 消息内容
 * @param {Object} options messageBox 配置
 * @return {Object} 返回then对象
 */
export function messageToLoginPage(message, options = {}) {
    return MessageBox.alert(message, '', {
        customClass: 'zt-message-box-alert',
        confirmButtonText: '刷新',
        ...options
    }).then(() => {
        window.location.href = RouterUtil.getPagePath(PageNames.LOGIN);
    });
}

// 消息提示后，3秒后自动跳登录页
export function messageToLoginPageWaitFor3S() {
    messageToLoginPage('您的权限发生了变化，3秒后将跳转到登录页', {
        confirmButtonText: '立即前往'
    });
    setTimeout(() => {
        window.location.href = RouterUtil.getPagePath(PageNames.LOGIN);
    }, 3000);
}

if (!String.prototype.trim) {
    String.prototype.trim = function () {
        return this.replace(/(^\s*)|(\s*$)/g, "");
    };
}

/**
 * 获取滚动条未知
 * @param {string} position 方向 例如：top
 * @return {number} 位置
 */
function getScroll(position) {
    let scroll = 0;
    position = 'scroll' + position[0].toUpperCase() + position.substr(1);
    if (document.documentElement && document.documentElement[position]) {
        scroll = document.documentElement[position];
    } else if (document.body) {
        scroll = document.body[position];
    }
    return scroll;
}

/**
 * 滚动到顶部
 * @param {boolean} animation 是否动画
 * @param {number} time 动画时长
 */
export function scrollToTop(animation = true, time = 2000) {
    if (!animation) {
        window.scrollTo(0, 0);
        return;
    }
    const frameOneSecond = 60; // 每秒帧数
    const requestAnimationFrame = window.requestAnimationFrame
        || window.webkitRequestAnimationFrame
        || window.mozRequestAnimationFrame
        || window.oRequestAnimationFrame
        || window.msRequestAnimationFrame
        || function (callback) {
            window.setTimeout(callback, 1000 / frameOneSecond);
        }
    const initTop = getScroll('top');
    const frame = time / frameOneSecond; // 帧数
    const step = initTop / frame; // 每帧的移动距离
    function scrollUp() {
        const top = getScroll('top');
        const left = getScroll('left');
        if (top > 0) {
            window.scrollTo(left, top - step);
            requestAnimationFrame(scrollUp);
        }
    }
    scrollUp();
}

export function validateFg(fg) {
    return /^([a-z0-9]{4})?-[a-z0-9]{4}$/.test(fg);
}

export function validateIp(ip) {
    // 辣鸡icode，一行不能超过120个字符
    const ipRegText = `^(\\d{1,2}|1\\d\\d|2[0-4]\\d|25[0-5])\\.(\\d{1,2}|1\\d\\d|2[0-4]\\d|25[0-5])\\.` +
    `(\\d{1,2}|1\\d\\d|2[0-4]\\d|25[0-5])\\.(\\d{1,2}|1\\d\\d|2[0-4]\\d|25[0-5])$`;
    const ipReg = new RegExp(ipRegText);
    const ipv6RegText = `^([\\da-fA-F]{1,4}:){6}((25[0-5]|2[0-4]\\d|[01]?\\d\\d?)\\.)` +
    `{3}(25[0-5]|2[0-4]\\d|[01]?\\d\\d?)$|^::([\\da-fA-F]{1,4}:){0,4}` +
    `((25[0-5]|2[0-4]\\d|[01]?\\d\d?)\\.){3}(25[0-5]|2[0-4]\\d|[01]?\\d\\d?)$` +
    `|^([\\da-fA-F]{1,4}:):([\\da-fA-F]{1,4}:){0,3}((25[0-5]|2[0-4]\\d|[01]?\\d\\d?)\\.)` +
    `{3}(25[0-5]|2[0-4]\\d|[01]?\\d\\d?)$|^([\\da-fA-F]{1,4}:){2}:([\\da-fA-F]{1,4}:)` +
    `{0,2}((25[0-5]|2[0-4]\\d|[01]?\\d\\d?)\\.){3}(25[0-5]|2[0-4]\\d|[01]?\\d\\d?)$|^` +
    `([\\da-fA-F]{1,4}:){3}:([\\da-fA-F]{1,4}:){0,1}((25[0-5]|2[0-4]\\d|[01]?\\d\\d?)\\.)` +
    `{3}(25[0-5]|2[0-4]\\d|[01]?\\d\\d?)$|^([\\da-fA-F]{1,4}:){4}:((25[0-5]|2[0-4]\\d|[01]?\\d\\d?)\\.)` +
    `{3}(25[0-5]|2[0-4]\\d|[01]?\\d\\d?)$|^([\\da-fA-F]{1,4}:){7}[\\da-fA-F]{1,4}$|^:((:[\\da-fA-F]{1,4})` +
    `{1,6}|:)$|^[\\da-fA-F]{1,4}:((:[\\da-fA-F]{1,4}){1,5}|:)$|^([\\da-fA-F]{1,4}:){2}((:[\\da-fA-F]{1,4}){1,4}|:)` +
    `$|^([\\da-fA-F]{1,4}:){3}((:[\\da-fA-F]{1,4}){1,3}|:)$|^([\\da-fA-F]{1,4}:){4}` +
    `((:[\\da-fA-F]{1,4}){1,2}|:)$|^([\\da-fA-F]{1,4}:){5}:([\\da-fA-F]{1,4})?$|^([\\da-fA-F]{1,4}:){6}:$`;
    const ipv6Reg = new RegExp(ipv6RegText);
    return ipReg.test(ip) || ipv6Reg.test(ip)
}

/**
 * prop默认空数组值
 * @return {Array} 空数组
 */
export function noopArr() {
    return [];
}

/**
 * prop默认空对象值
 * @return {Object} 空对象
 */
export function noop() {
    return {};
}

export function noopFn() {
    return function() {}
}

export function getPercentText(val, sum) {
    let percent = 0.0;
    if (sum > 0) {
        percent = (100 * val / sum).toFixed(2);
        if (percent === '0.00' && val != 0) {
            percent = '<0.01';
        }
    }
    return `${percent}%`;
}

export function percentFormat(val) {
    if (!val) {
        return '0%';
    }
    val = parseFloat(val);
    if (val <= 0) {
        return '0%';
    }
    if (val < 0.0001) {
        return '<0.01%';
    }
    return (val * 100).toFixed(2) + '%';
}

export function getLogoutUrl() {
    const loginType = getCookie(LoginTypeCookieKey);
    if (loginType === AccountTypes.UUAP) {
        return uuapLogoutUrl;
    } else {
        return passportLogoutUrl;
    }
}